import React, { ChangeEvent, useState, forwardRef, ReactNode } from 'react'

import parsePhoneNumberFromString from 'libphonenumber-js'
import { Field, FormInstance } from 'rc-field-form'
import { Meta, Rule, RuleObject, StoreValue } from 'rc-field-form/es/interface.d'
import styled, { css, CSS, useTheme } from 'styled-components'

import { Icon, IconEnum, IconSizeEnum, SmallLoader } from '@atoms/index'
import { SiteHelper } from '@client/lib/SiteHelper'
import { ResponsivePXValue, LiteBoxShadow } from '@components/Theme'

import { FormContext, FormContextProperties, FormValue, InputProps, RuleType } from './Form'
import { InputWrapper } from './InputWrapper'
import { AsteriskContainer, AsteriskLabel, RequiredAsterisk } from './SelectInput'

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .right-icon {
    position: absolute;

    ${ResponsivePXValue('right', '16px')}
  }

 `

const Disabled = css`
  border-color: ${(props): string => props.theme.colors.whites.silver};
  color: ${(props): string => props.theme.colors.whites.silver};
  background-color: ${(props): string => props.theme.colors.whites.alabaster};
  ::placeholder {
    color: ${(props): string => props.theme.colors.whites.silver};
    opacity: 1;
  }
`

const TextInputElement = styled.input<{ $error: boolean, disabled: boolean, $textAlign: string }>`
  font-family: 'gordita';
  font-weight: 400;
  border-radius: 4px;
  border-style: solid;
  border-width: 1px;

  text-align: ${(props): string => props.$textAlign};
  background-color: ${(props): string => props.$error ? props.theme.colors.whites.roseWhite : props.theme.colors.whites.pureWhite};
  border-color: ${(props): string => props.$error ? props.theme.colors.reds.cautionRed : props.theme.colors.whites.desertStorm};

  ${ResponsivePXValue('font-size', '14px')}
  ${ResponsivePXValue('padding', '0 12px')}
  ${ResponsivePXValue('width', 'CALC(100% - 26px)')}
  ${ResponsivePXValue('height', '40px')}

  &:focus {
    outline: none;
    box-shadow: none;
    border-color: ${(props): string => props.$error ? props.theme.colors.reds.cautionRed : props.theme.colors.slates.bitter};
    ${LiteBoxShadow}
  }

  &::placeholder {
    color: ${(props): string => props.theme.colors.whites.silver};
    opacity: 1;
    ${ResponsivePXValue('line-height', { mobile: '40px', tablet: '40px', desktop: '40px' })}
    transform: translateY(2px);
  }

  ${(props): CSS => {
    if (props.disabled) {
      return Disabled
    }
  }}

`
const IconContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`

const LoadingContainer = styled.div`
  position: absolute;
  ${ResponsivePXValue('right', '8px')}
  ${ResponsivePXValue('top', '8px')}
  ${ResponsivePXValue('width', '25px')}
  ${ResponsivePXValue('height', '25px')}
`

interface Values {
  value?: FormValue
  onChange?: (value: FormValue) => void
  onBlur?: (value: FormValue) => void
}

export interface TextInputProps extends InputProps {
  rules?: Rule[]
  showOptional?: boolean
  placeholder?: string
  variant?: 'text' | 'email' | 'phone' | 'password' | 'integer' | 'float' | 'creditCard'
  textAlign?: 'left' | 'right' | 'center'
  rightIcon?: IconEnum
  maxLength?: number
  clearOnRightIconClick?: boolean
  className?: string
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onRightIconClick?: () => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onBlurError?: boolean
  absoluteError?: boolean
  inputMode?: 'text' | 'numeric' | 'decimal' | 'tel' | 'search' | 'email' | 'url' | 'none'
}

interface TextInputState {
  passwordVisibility: boolean
  error: string
}

const DEFAULT_STATE = {
  passwordVisibility: false,
  error: '',
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(props, ref) {

  let { placeholder = '', label, rules, showLabel, showOptional = false, name, variant = 'text', textAlign = 'left', clearOnRightIconClick = false, rightIcon, loading, disabled, className, onKeyDown, onRightIconClick, onBlurError, absoluteError, inputMode: inputModeProp } = props

  const [state, setState] = useState<TextInputState>({ ...DEFAULT_STATE })
  const theme = useTheme()
  const type = !state.passwordVisibility && variant === 'password' ? 'password' : 'text'
  const isPassword = variant === 'password'
  const passwordIcon = state.passwordVisibility ? IconEnum.EYE_OFF : IconEnum.EYE

  // Use inputMode prop if provided, otherwise default to 'text'
  let inputMode: 'none' | 'text' | 'search' | 'email' | 'url' | 'numeric' | 'tel' | 'decimal' = inputModeProp || 'text'
  let pattern: string | undefined
  if (inputMode === 'numeric') {
    pattern = '[0-9]*'
  } else if (variant === 'phone') {
    inputMode = 'tel'
  }

  return (
    <Field name={name} rules={rules}>
      {(control: Values, meta: Meta, form: FormInstance) => {
        if (!rules) {
          rules = []
        }
        const hasRule = rules.find((rule: { type: RuleType }) => rule.type === 'method')
        if (!hasRule) {
          rules.push({
            type: 'method',
            validateTrigger: 'onSubmit',
            validator: (rule: RuleObject, value: StoreValue, callback: (error?: string) => void): void => {
              let error: string | undefined
              if (variant === 'email') {
                const isValid = SiteHelper.validateEmail(value)
                if (!isValid) {
                  error = 'Please enter a valid email address'
                }
              } else if (variant === 'phone') {
                const isValid = SiteHelper.validatePhone(value)
                if (!isValid) {
                  error = 'Please enter a valid phone number'
                }
              }
              callback(error)
            },
          })
        }
        const required = !!(
          rules &&
          rules.some(rule => {
            if (rule && typeof rule === 'object' && rule.required) {
              return true
            }
            if (typeof rule === 'function') {
              const ruleEntity = rule(form)
              return ruleEntity && ruleEntity.required
            }
            return false
          })
        )
        const error = meta.errors?.[0]
        const { value, onChange, onBlur } = control

        const format = (val: string): string | number | undefined => {
          if (val === undefined) {
            return
          }
          if (variant === 'integer') {
            return parseInt(val)
          } else if (variant === 'float') {
            return parseFloat(val)
          } else if (variant === 'email') {
            return val
          } else if (variant === 'phone') {
            let newNumber = null
            if (val.indexOf(' ') !== -1) {
              const values = val.split(' ')
              const prefix = SiteHelper.getCountryCodeForTelPrefix(values.shift())
              const number = values.join(' ')
              try {
                const phoneNumber = parsePhoneNumberFromString(number, prefix)
                newNumber = phoneNumber.formatInternational()
              } catch (err) { }
            } else {
              newNumber = val
              try {
                const phoneNumber = parsePhoneNumberFromString(newNumber, 'ZA')
                newNumber = phoneNumber.formatInternational()
              } catch (err) { }
            }
            return newNumber
          } else if (variant === 'creditCard') {
            if (val.length > 0) {
              const cardNum = val.replace(/\s/g, '')
              let checkedNum = parseInt(cardNum) + ''
              if (checkedNum === 'NaN') {
                return ''
              }
              if (checkedNum.length > 16) {
                checkedNum = checkedNum.substr(0, 16)
              }
              let returnString = ''
              for (let s = 0; s < checkedNum.length; s++) {
                if (s % 4 === 0 && s !== 0) {
                  returnString += ' '
                }
                returnString += checkedNum.charAt(s)
              }
              return returnString
            }
          } else {
            return val
          }
        }

        const _handleChange = (e: ChangeEvent<HTMLInputElement>) => {
          let newValue = e.target.value
          if (props.maxLength && newValue.length > props.maxLength) {
            newValue = newValue.slice(0, props.maxLength)
          }
          const parsed = format(newValue)
          onChange(parsed)
        }

        const _handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
          const raw = e.target.value.replace(/\D/g, '').slice(0, 4)
          const formatted = format(raw)
          if (raw.length >= 2) {
            const mm = parseInt(raw.slice(0, 2), 10)
            if (mm < 1 || mm > 12) {
              return
            }
          }
          const error = meta.errors?.[0]
          setState((prevState) => ({
            ...prevState,
            error,
          }))
          onBlur?.(formatted)
        }

        const _handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (onKeyDown) {
            onKeyDown(e)
          }
        }

        const _togglePasswordView = () => {
          setState((prevState) => ({ ...prevState, passwordVisibility: !prevState.passwordVisibility }))
        }

        const _rightIconClickHandler = () => {
          if (rightIcon) {
            if (clearOnRightIconClick) {
              form.setFieldsValue({ [name]: '' })
            }
            if (onRightIconClick) {
              onRightIconClick()
            }
          }
        }

        const getInputLabel = (label: string): ReactNode | string => {
          if (required && !!label) {
            return (
              <AsteriskContainer>
                <AsteriskLabel>{label}</AsteriskLabel>
                <RequiredAsterisk>*</RequiredAsterisk>
              </AsteriskContainer>
            )
          }
          return label
        }

        const inputLabel = getInputLabel(label)

        const shouldShowRightIcon = isPassword || (!!rightIcon && (!clearOnRightIconClick || (clearOnRightIconClick && value)))

        return (
          <InputWrapper
            required={required}
            label={inputLabel}
            showLabel={showLabel}
            showOptional={showOptional}
            error={onBlurError ? state.error : error}
            absoluteError={absoluteError}
            className={className}>
            <FormContext.Consumer>
              {({ loading: formLoading, disabled: formDisabled }: FormContextProperties) => (
                <Container>
                  <TextInputElement
                    ref={ref}
                    autoComplete='on'
                    disabled={loading || disabled || formLoading || formDisabled}
                    name={name}
                    $error={onBlurError ? !!state.error : !!error}
                    type={type}
                    value={value ? value + '' : ''}
                    placeholder={placeholder}
                    $textAlign={textAlign}
                    onChange={_handleChange}
                    onKeyDown={_handleKeyDown}
                    onBlur={_handleBlur}
                    inputMode={inputMode}
                    pattern={pattern}
                  />
                  <If condition={(onBlurError ? !!state.error : !!error) || shouldShowRightIcon}>
                    <Choose>
                      <When condition={isPassword}>
                        <IconContainer onClick={_togglePasswordView}>
                          <Icon
                            containerClassName='right-icon'
                            size={IconSizeEnum.SMALL}
                            icon={passwordIcon}
                            color={theme.colors.whites.desertStorm} />
                        </IconContainer>
                      </When>
                      <When condition={onBlurError ? !!state.error : !!error}>
                        <IconContainer>
                          <Icon
                            containerClassName='right-icon'
                            size={IconSizeEnum.SMALL}
                            icon={IconEnum.WARNING_OUTLINE}
                            color={theme.colors.reds.cautionRed} />
                        </IconContainer>
                      </When>
                      <Otherwise>
                        <IconContainer onClick={_rightIconClickHandler}>
                          <Icon
                            containerClassName='right-icon'
                            size={IconSizeEnum.SMALL}
                            icon={rightIcon} />
                        </IconContainer>
                      </Otherwise>
                    </Choose>
                  </If>
                  <If condition={loading || formLoading}>
                    <LoadingContainer>
                      <SmallLoader
                        color={theme.colors.greens.aquaDeep} />
                    </LoadingContainer>
                  </If>
                </Container>
              )}
            </FormContext.Consumer>
          </InputWrapper>
        )
      }}
    </Field>
  )
})
